import { useState } from 'react';
import { generateCourseMetadataWithAiApi } from '../../../../api/apiServices';
import { getFriendlyErrorMessage } from '../../../../utils/getFriendlyErrorMessage';
import CategorySelect from '../../../../components/base/CategorySelect';
import { useOrg } from '../../../../context/OrgContext';


export default function CourseBasicForm({ course, onChange, onThumbnailChange,
  onInstructorAvatarChange }) {
  const { organizations } = useOrg();
  const [tagInput, setTagInput] = useState('');
  const [learnInput, setLearnInput] = useState('');
  const [reqInput, setReqInput] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [instructorAvatarFile, setInstructorAvatarFile] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiNotice, setAiNotice] = useState('');

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !course.tags.includes(tag)) {
      onChange({ tags: [...course.tags, tag] });
    }
    setTagInput('');
  };

  const addLearn = () => {
    const item = learnInput.trim();
    if (item) {
      onChange({ whatYouLearn: [...course.whatYouLearn, item] });
    }
    setLearnInput('');
  };

  const addReq = () => {
    const item = reqInput.trim();
    if (item) {
      onChange({ requirements: [...course.requirements, item] });
    }
    setReqInput('');
  };

  // Handle Thumbnail Upload
  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onThumbnailChange(file);                    // Pass file to parent
      const previewUrl = URL.createObjectURL(file);
      onChange({ thumbnail: previewUrl });        // Show preview
    }
  };

  // Handle Instructor Avatar Upload
  const handleInstructorAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onInstructorAvatarChange(file);             // Pass file to parent
      const previewUrl = URL.createObjectURL(file);
      onChange({ instructorAvatar: previewUrl });
    }
  };

  // Fills in the short/long description, learning outcomes, requirements,
  // and thumbnail from just the course title (plus category/level if
  // already chosen) using AI. Everything it fills in stays fully editable
  // afterward -- this is a head start, not a lock-in.
  const handleGenerateWithAi = async () => {
    if (!course.title?.trim()) {
      setAiError('Enter a course title first.');
      return;
    }
    setAiLoading(true);
    setAiError('');
    setAiNotice('');
    try {
      const res = await generateCourseMetadataWithAiApi({
        title: course.title.trim(),
        category: course.category,
        level: course.level,
      });
      if (!res.success) {
        throw new Error(res.message || 'Failed to generate course details.');
      }

      onChange({
        description: res.description || course.description,
        longDescription: res.longDescription || course.longDescription,
        whatYouLearn: res.whatYouLearn?.length ? res.whatYouLearn : course.whatYouLearn,
        requirements: res.requirements?.length ? res.requirements : course.requirements,
        ...(res.thumbnail ? { thumbnail: res.thumbnail } : {}),
      });

      if (res.thumbnail) {
        // The AI thumbnail is already a hosted URL, not a local file -- clear
        // any previously-picked file so Save doesn't send that instead.
        onThumbnailChange(null);
      }

      setAiNotice(
        res.thumbnailFailed
          ? 'Filled in the course details, but the AI thumbnail could not be generated -- you can upload one manually.'
          : 'Filled in the description, learning outcomes, requirements, and thumbnail -- feel free to edit any of it.'
      );
    } catch (err) {
      console.error('AI metadata generation failed:', err);
      setAiError(getFriendlyErrorMessage(err, 'Something went wrong generating course details.'));
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Course Information */}
          <div className="bg-white rounded-xl border border-slate-100 p-6 space-y-4">
            <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100">
              Course Information
            </h2>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-600">Course Title *</label>
                <button
                  type="button"
                  onClick={handleGenerateWithAi}
                  disabled={aiLoading || !course.title?.trim()}
                  className="flex items-center gap-1 text-[11px] font-bold text-teal-700 hover:text-teal-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  title="Generate the description, learning outcomes, requirements, and thumbnail from this title"
                >
                  {aiLoading ? (
                    <><i className="ri-loader-4-line animate-spin" /> Generating...</>
                  ) : (
                    <><i className="ri-magic-line" /> Generate with AI</>
                  )}
                </button>
              </div>
              <input
                type="text"
                value={course.title || ''}
                onChange={e => onChange({ title: e.target.value })}
                placeholder="e.g. Complete React & TypeScript Masterclass"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
              />
              {aiError && (
                <p className="text-[11px] text-rose-600 mt-1.5 flex items-start gap-1">
                  <i className="ri-error-warning-line mt-0.5" /> {aiError}
                </p>
              )}
              {!aiError && aiNotice && (
                <p className="text-[11px] text-teal-700 mt-1.5 flex items-start gap-1">
                  <i className="ri-checkbox-circle-line mt-0.5" /> {aiNotice}
                </p>
              )}
              <p className="text-[11px] text-slate-400 mt-1.5">
                Type a title, then use Generate with AI to fill in the description, learning outcomes, requirements, and thumbnail below -- everything stays editable.
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Short Description *</label>
              <textarea
                value={course.description || ''}
                onChange={e => onChange({ description: e.target.value })}
                placeholder="Brief description (shown in course cards)"
                rows={2}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 resize-none bg-white"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Long Description</label>
              <textarea
                value={course.longDescription || ''}
                onChange={e => onChange({ longDescription: e.target.value })}
                placeholder="Detailed course description (shown on course detail page)"
                rows={4}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 resize-none bg-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Category</label>
                <CategorySelect
                  value={course.category}
                  onChange={(category) => onChange({ category })}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Level</label>
                <select
                  value={course.level || 'Beginner'}
                  onChange={e => onChange({ level: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Duration</label>
              <input
                type="text"
                value={course.duration || ''}
                onChange={e => onChange({ duration: e.target.value })}
                placeholder="e.g. 24 hours or 8 weeks"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Organization</label>
              <select
                value={course.organizationId || ''}
                onChange={e => onChange({ organizationId: e.target.value || null })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
              >
                <option value="">Personal (no organization)</option>
                {organizations.map(org => (
                  <option key={org._id} value={org._id}>{org.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Which organization's public catalog this course appears under. Only organizations you belong to are listed here.
              </p>
            </div>

            <div>
              <label className="flex items-center gap-2.5 p-2.5 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                <input
                  type="checkbox"
                  checked={course.issueCertificate !== false}
                  onChange={e => onChange({ issueCertificate: e.target.checked })}
                  className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-xs font-medium text-slate-700">Issue a certificate when a student completes this course</span>
              </label>
              <p className="text-xs text-slate-400 mt-1">
                The certificate's look (logo, colors, signature, wording) comes from this course's organization branding settings. Turn this off for courses that shouldn't award one, such as a short primer.
              </p>
            </div>
          </div>

          {/* Course Pricing & Monetization Section */}
          <div className="bg-white rounded-xl border border-slate-100 p-6 space-y-4">
            <div className="flex items-center justify-between pb-1 border-b border-slate-100">
              <h2 className="text-sm font-bold text-slate-700">
                Course Pricing & Monetization
              </h2>
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${
                course.isPaid ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'
              }`}>
                {course.isPaid ? 'Paid Course' : 'Free Course'}
              </span>
            </div>

            {/* Pricing Type Selector */}
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-2">Pricing Model</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => onChange({ isPaid: false, price: 0, hasDiscount: false, discount: 0 })}
                  className={`p-3.5 rounded-xl border-2 text-left transition-all cursor-pointer flex items-center justify-between ${
                    !course.isPaid
                      ? 'border-teal-600 bg-teal-50/50 text-teal-900'
                      : 'border-slate-200 hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      !course.isPaid ? 'border-teal-600 bg-teal-600' : 'border-slate-300'
                    }`}>
                      {!course.isPaid && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <div>
                      <p className="text-xs font-bold">Free Course</p>
                      <p className="text-[11px] text-slate-500">Accessible to all enrolled students at no cost</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-emerald-600">$0</span>
                </button>

                <button
                  type="button"
                  onClick={() => onChange({ isPaid: true, price: course.price || 49 })}
                  className={`p-3.5 rounded-xl border-2 text-left transition-all cursor-pointer flex items-center justify-between ${
                    course.isPaid
                      ? 'border-indigo-600 bg-indigo-50/50 text-indigo-900'
                      : 'border-slate-200 hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      course.isPaid ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'
                    }`}>
                      {course.isPaid && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <div>
                      <p className="text-xs font-bold">Paid Course</p>
                      <p className="text-[11px] text-slate-500">Charge students a one-time enrollment fee</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-indigo-600">Paid</span>
                </button>
              </div>
            </div>

            {/* Paid Details Configuration */}
            {course.isPaid && (
              <div className="space-y-4 pt-2 border-t border-slate-100 animate-fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 block mb-1.5">
                      Base Price ($ USD) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">$</span>
                      <input
                        type="number"
                        min={1}
                        step="1"
                        value={course.price || ''}
                        onChange={e => onChange({ price: Math.max(0, Number(e.target.value) || 0) })}
                        placeholder="e.g. 49"
                        className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 font-semibold focus:outline-none focus:border-indigo-500 bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-600 block mb-1.5">
                      Promotional Discount Option
                    </label>
                    <label className="flex items-center gap-2.5 p-2.5 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={Boolean(course.hasDiscount)}
                        onChange={e => onChange({ 
                          hasDiscount: e.target.checked,
                          discount: e.target.checked ? (course.discount || 20) : 0 
                        })}
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                      />
                      <span className="text-xs font-medium text-slate-700">Offer a discount to students</span>
                    </label>
                  </div>
                </div>

                {course.hasDiscount && (
                  <div className="p-4 bg-amber-50/70 border border-amber-200/80 rounded-xl space-y-3 animate-fade-in">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <label className="text-xs font-bold text-amber-900 block mb-1">
                          Discount Percentage (%)
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            max={99}
                            value={course.discount || ''}
                            onChange={e => {
                              const val = Math.min(99, Math.max(0, Number(e.target.value) || 0));
                              onChange({ discount: val });
                            }}
                            placeholder="e.g. 20"
                            className="w-24 px-3 py-2 border border-amber-300 rounded-lg text-sm font-bold text-slate-800 focus:outline-none focus:border-amber-500 bg-white"
                          />
                          <span className="text-xs font-bold text-amber-800">% OFF</span>
                        </div>
                      </div>

                      {/* Live Calculated Price Summary */}
                      <div className="bg-white p-3 rounded-lg border border-amber-200 text-right">
                        <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Student Pays</span>
                        <div className="flex items-baseline justify-end gap-1.5 mt-0.5">
                          <span className="text-xs text-slate-400 line-through">${Number(course.price || 0).toFixed(2)}</span>
                          <span className="text-base font-extrabold text-emerald-700">
                            ${Math.max(0, Number(course.price || 0) * (1 - (Number(course.discount || 0) / 100))).toFixed(2)}
                          </span>
                        </div>
                        <span className="text-[10px] text-amber-700 font-semibold">
                          You save them ${(Number(course.price || 0) * (Number(course.discount || 0) / 100)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Instructor Section */}
          <div className="bg-white rounded-xl border border-slate-100 p-6 space-y-4">
            <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100">Instructor</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Instructor Name</label>
                <input
                  type="text"
                  value={course.instructor || ''}
                  onChange={e => onChange({ instructor: e.target.value })}
                  placeholder="Full name"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Instructor Avatar</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleInstructorAvatarSelect}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                {course.instructorAvatar && (
                  <div className="mt-2 w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={course.instructorAvatar} alt="preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Bio</label>
              <textarea
                value={course.instructorBio || ''}
                onChange={e => onChange({ instructorBio: e.target.value })}
                placeholder="Short instructor biography"
                rows={2}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 resize-none bg-white"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100 mb-4">What You&apos;ll Learn</h2>
            <div className="space-y-2 mb-3">
              {course.whatYouLearn.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-teal-500 flex-shrink-0">
                    <i className="ri-check-line text-xs" />
                  </div>
                  <span className="text-sm text-slate-700 flex-1">{item}</span>
                  <button
                    onClick={() => onChange({ whatYouLearn: course.whatYouLearn.filter((_, i) => i !== idx) })}
                    className="w-5 h-5 flex items-center justify-center text-slate-300 hover:text-rose-400 cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={learnInput}
                onChange={e => setLearnInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addLearn()}
                placeholder="Add a learning objective..."
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
              />
              <button
                onClick={addLearn}
                className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer hover:bg-teal-700 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100 mb-4">Requirements</h2>
            <div className="space-y-2 mb-3">
              {course.requirements.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-slate-400 flex-shrink-0">
                    <i className="ri-circle-fill text-xs" />
                  </div>
                  <span className="text-sm text-slate-700 flex-1">{item}</span>
                  <button
                    onClick={() => onChange({ requirements: course.requirements.filter((_, i) => i !== idx) })}
                    className="w-5 h-5 flex items-center justify-center text-slate-300 hover:text-rose-400 cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={reqInput}
                onChange={e => setReqInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addReq()}
                placeholder="Add a requirement..."
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
              />
              <button
                onClick={addReq}
                className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer hover:bg-teal-700 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-5">
          {/* Thumbnail Upload */}
          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h2 className="text-sm font-bold text-slate-700 mb-3">Thumbnail</h2>
            
            {course.thumbnail && (
              <div className="mb-3 rounded-lg overflow-hidden h-40 border border-slate-200">
                <img 
                  src={course.thumbnail} 
                  alt="thumbnail preview" 
                  className="w-full h-full object-cover" 
                />
              </div>
            )}

            <label className="text-xs font-semibold text-slate-600 block mb-1.5">Upload Thumbnail (16:9 recommended)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailSelect}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
            />
            <p className="text-xs text-slate-500 mt-2">Max size: 5MB</p>
          </div>

          {/* Tags Section - unchanged */}
          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h2 className="text-sm font-bold text-slate-700 mb-3">Tags</h2>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {course.tags?.map(tag => (
                <span
                  key={tag}
                  className="flex items-center gap-1 text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full"
                >
                  {tag}
                  <button
                    onClick={() => onChange({ tags: course.tags.filter(t => t !== tag) })}
                    className="w-3 h-3 flex items-center justify-center hover:text-rose-500 cursor-pointer ml-0.5"
                  >
                    <i className="ri-close-line" style={{ fontSize: '10px' }} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTag()}
                placeholder="Add tag..."
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
              />
              <button
                onClick={addTag}
                className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer hover:bg-teal-700 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-100 rounded-xl p-4">
            <div className="flex items-start gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center text-teal-500 flex-shrink-0 mt-0.5">
                <i className="ri-information-line" />
              </div>
              <div>
                <p className="text-xs font-semibold text-teal-800">Tips</p>
                <ul className="text-xs text-teal-700 mt-1.5 space-y-1">
                  <li>&bull; Use clear, specific course titles</li>
                  <li>&bull; Add at least 4 learning objectives</li>
                  <li>&bull; Include prerequisite requirements</li>
                  <li>&bull; Upload a high-quality 16:9 thumbnail</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
